/**
 * Stellplatz-Modus der Belegung (Board „Belegung · Stellplätze“): Kopf „Stellplätze · Objekt“ mit
 * Hinweis, Raster in Apartment-Maßen, dunkle Leiste mit der Auswahl. Ladefehler mit Grund und
 * „Erneut laden“, ohne Plätze ein Weg zu den Parkplätzen. Das Objekt wählt der Aufrufer.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useApi, useApiProblem } from '@/shared/api';
import type { PropertyGroupResponse } from '@/shared/api';
import { SurfaceLoadError, SurfaceLoading, SurfaceStaleHint } from '@/shared/components';
import { ParkingPeriodDialog } from '@/features/property-groups/parking-occupancy/ParkingPeriodDialog';
import { ParkingReleaseDialog } from '@/features/property-groups/parking-occupancy/ParkingReleaseDialog';
import { addDays } from '@/features/property-groups/parking-occupancy/parkingOccupancyHelpers';
import {
  parkingAccessDenied,
  parkingProblemText,
} from '@/features/property-groups/parking-occupancy/parkingProblemText';
import { ParkingCalendarGrid } from './ParkingCalendarGrid';
import { ParkingSelectionBar } from './ParkingSelectionBar';
import { ParkingSpotOverview } from './ParkingSpotOverview';

interface Props {
  group: PropertyGroupResponse;
  /** Die sichtbare Zeitachse – dieselbe, die der Apartment-Modus zeigt. */
  dates: string[];
  /** Heute in der Zeitzone des Objekts – für die Spalte und die Vorgabe im Dialog. */
  today: string;
  density: 3 | 7 | 14;
}

export function ParkingCalendarSection({ group, dates, today, density }: Props) {
  const api = useApi();
  const from = dates[0] ?? '';
  // Der Server liest `to` exklusiv (wie jeden Zeitraum hier) – sonst fehlte, was am letzten
  // sichtbaren Tag beginnt.
  const to = addDays(dates[dates.length - 1] ?? from, 1);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<'create' | 'release' | null>(null);
  const [overviewId, setOverviewId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['parking-occupancy', group.id, from, to],
    queryFn: () => api.parking.occupancy(group.id, from, to),
    enabled: dates.length > 0,
  });
  const problem = useApiProblem(query.error ?? null);
  const spots = useMemo(() => query.data?.spots ?? [], [query.data]);
  // Ein Platz, der inzwischen weg ist, lebt nicht in der Auswahl weiter.
  const chosen = useMemo(
    () => spots.filter((spot) => selected.has(spot.spotId)),
    [spots, selected],
  );
  // Das Blatt folgt dem neu geladenen Stand der Zeile.
  const overview = spots.find((spot) => spot.spotId === overviewId) ?? null;
  const offline = !query.data && query.fetchStatus === 'paused';
  const stale = Boolean(query.data) && (query.isError || query.isPaused);
  const toggle = (spotId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(spotId)) next.add(spotId);
      return next;
    });

  return (
    <section aria-label="Belegung der Stellplätze" className="relative">
      <div className="border-hairline flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b px-[18px] py-3.5">
        <h2 className="text-[15px] font-bold">Stellplätze · {group.name}</h2>
        <span className="text-ink-soft text-[13px]">
          Zeilen anhaken, um Sperren anzulegen oder eine Buchung zuzuordnen.
        </span>
      </div>
      {stale && (
        <SurfaceStaleHint onRetry={() => void query.refetch()} retrying={query.isFetching} />
      )}
      {offline || (query.isError && !query.data) ? (
        <SurfaceLoadError
          message={
            offline
              ? 'Keine Verbindung – die Stellplätze sind nicht geladen.'
              : parkingProblemText(problem, 'Die Stellplatz-Belegung konnte nicht geladen werden.')
          }
          onRetry={
            !offline && parkingAccessDenied(problem) ? undefined : () => void query.refetch()
          }
          retrying={query.isFetching}
        />
      ) : query.isPending ? (
        <SurfaceLoading label="Stellplätze werden geladen" rows={4} />
      ) : spots.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 px-5 py-12 text-center">
          <b className="text-base">Noch keine Stellplätze</b>
          <span className="text-ink-soft text-[13px]">
            {group.name} hat keine Stellplätze. Lege sie unter Parkplätze an und weise Apartments
            zu.
          </span>
          <Link to="/parking" className="text-forest mt-1 min-h-11 content-center font-bold">
            Zu den Parkplätzen
          </Link>
        </div>
      ) : (
        <ParkingCalendarGrid
          spots={spots}
          dates={dates}
          today={today}
          density={density}
          selected={selected}
          onToggle={toggle}
          onToggleAll={() =>
            setSelected(
              chosen.length === spots.length
                ? new Set()
                : new Set(spots.map((spot) => spot.spotId)),
            )
          }
          onOverview={(spot) => setOverviewId(spot.spotId)}
        />
      )}
      <ParkingSelectionBar
        selectedCount={chosen.length}
        total={spots.length}
        onClear={() => setSelected(new Set())}
        onCreate={() => setDialog('create')}
        onRelease={() => setDialog('release')}
      />
      <ParkingSpotOverview
        spot={overview}
        dates={dates}
        onOpenChange={(open) => !open && setOverviewId(null)}
      />
      {dialog === 'release' && (
        <ParkingReleaseDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          propertyGroupId={group.id}
          rows={chosen}
          stale={stale}
        />
      )}
      {dialog === 'create' && (
        <ParkingPeriodDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          propertyGroupId={group.id}
          today={today}
          spotIds={chosen.map((spot) => spot.spotId)}
          spotLabels={chosen.map((spot) => spot.label)}
        />
      )}
    </section>
  );
}
